'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import classNames from 'classnames';
import { CALYPSO_MEDIUM, BUTTON_DISABLED_TEXT } from 'HubStyleTokens/colors';
import { SELECTABLE_BOX_SHADOW_COLOR, SELECTABLE_BOX_SELECTED_BACKGROUND_COLOR, SELECTABLE_BOX_SELECTED_BORDER_COLOR, BUTTON_DISABLED_BACKGROUND_COLOR } from 'HubStyleTokens/theme';
import { MICRO_TRANSITION_TIMING } from 'HubStyleTokens/times';
import * as CustomRenderer from 'UIComponents/utils/propTypes/customRenderer';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import SyntheticEvent from 'UIComponents/core/SyntheticEvent';
import Controllable from 'UIComponents/decorators/Controllable';
import Small from 'UIComponents/elements/Small';
import UIImage from 'UIComponents/image/UIImage';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIHoverlay from 'UIComponents/overlay/UIHoverlay';
import UIBreakString from 'UIComponents/text/UIBreakString';
import HoverProvider from 'UIComponents/providers/HoverProvider';
import ActiveProvider from 'UIComponents/providers/ActiveProvider';
import Footer from './Footer';
import Price from './Price';
const iconMaxHeight = 48;
const StyledUICardWrapper = styled(UICardWrapper).withConfig({
  displayName: "AbstractCard__StyledUICardWrapper",
  componentId: "sc-1opehhh-0"
})(["&&{&.is--selectable{cursor:pointer;border:2px solid transparent;transition:border ", " ease-out;&.is--hovered{border-color:", ";box-shadow:0 0 12px 0 ", ";}&.is--selected{background-color:", ";border-color:", ";}&.is--disabled{background-color:", ";border-color:", ";box-shadow:none;color:", " !important;cursor:not-allowed;}}}"], MICRO_TRANSITION_TIMING, CALYPSO_MEDIUM, SELECTABLE_BOX_SHADOW_COLOR, SELECTABLE_BOX_SELECTED_BACKGROUND_COLOR, SELECTABLE_BOX_SELECTED_BORDER_COLOR, BUTTON_DISABLED_BACKGROUND_COLOR, BUTTON_DISABLED_BACKGROUND_COLOR, BUTTON_DISABLED_TEXT);

const DefaultFooter = ({
  installs,
  price
}) => {
  return /*#__PURE__*/_jsxs(Footer, {
    children: [/*#__PURE__*/_jsx(Small, {
      use: "help",
      children: installs
    }), /*#__PURE__*/_jsx(Price, {
      value: price
    })]
  });
};

const DefaultHeader = ({
  title
}) => /*#__PURE__*/_jsx("span", {
  className: "m-bottom-0 is--heading-4",
  children: title
});

const AbstractCard = props => {
  const {
    bodyComponent,
    className,
    disabled,
    footerComponent,
    footerHoverlayContent,
    headingComponent,
    iconUrl,
    imageSectionClassName,
    installs,
    onClick,
    onSelectedChange,
    price,
    ribbonComponent,
    selectable,
    selected,
    statusComponent,
    subtitle,
    title,
    titleHelpText
  } = props;
  const handleClick = useCallback(event => {
    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (selectable) {
      onSelectedChange(SyntheticEvent(!selected));
    }

    if (onClick) onClick(event);
  }, [disabled, onClick, onSelectedChange, selectable, selected]);
  return /*#__PURE__*/_jsx(HoverProvider, Object.assign({}, props, {
    children: hoverProviderProps => /*#__PURE__*/_jsx(ActiveProvider, Object.assign({}, props, {
      children: activeProviderProps => {
        const {
          active
        } = activeProviderProps,
              activeProviderRestProps = _objectWithoutPropertiesLoose(activeProviderProps, ["active"]);

        const {
          hovered
        } = hoverProviderProps,
              hoverProviderRestProps = _objectWithoutPropertiesLoose(hoverProviderProps, ["hovered"]);

        const computedClassName = classNames(className, active && 'is--active', disabled && 'is--disabled', hovered && 'is--hovered', selectable && 'is--selectable', selected && 'is--selected');

        let renderedFooter = /*#__PURE__*/_jsx(UICardSection, {
          className: "p-top-5",
          children: CustomRenderer.render(footerComponent, {
            installs,
            price
          })
        });

        if (footerHoverlayContent) {
          renderedFooter = /*#__PURE__*/_jsx(UIHoverlay, {
            foregroundContent: /*#__PURE__*/_jsx(UIFlex, {
              align: "center",
              justify: "center",
              children: footerHoverlayContent
            }),
            hovered: hovered,
            use: "bottom-half-card",
            children: renderedFooter
          });
        }

        const renderedBody = bodyComponent ? CustomRenderer.render(bodyComponent) : null;
        return /*#__PURE__*/_jsxs(StyledUICardWrapper, Object.assign({}, activeProviderRestProps, {}, hoverProviderRestProps, {
          "aria-checked": selectable ? selected : undefined,
          "aria-disabled": disabled,
          className: computedClassName,
          compact: true,
          onClick: handleClick,
          role: selectable ? 'checkbox' : undefined,
          children: [ribbonComponent && CustomRenderer.render(ribbonComponent), /*#__PURE__*/_jsx(UICardSection, {
            className: classNames(imageSectionClassName, 'p-bottom-4'),
            children: /*#__PURE__*/_jsxs(UIFlex, {
              justify: "between",
              children: [/*#__PURE__*/_jsx(UIImage, {
                src: iconUrl,
                style: {
                  maxHeight: iconMaxHeight
                }
              }), statusComponent && CustomRenderer.render(statusComponent)]
            })
          }), /*#__PURE__*/_jsxs(UICardSection, {
            className: "p-bottom-0",
            scrollable: true,
            children: [/*#__PURE__*/_jsxs("div", {
              className: "m-bottom-2",
              children: [/*#__PURE__*/_jsx(UIBreakString, {
                children: CustomRenderer.render(headingComponent, {
                  title
                })
              }), titleHelpText && /*#__PURE__*/_jsx(Small, {
                use: "help",
                children: titleHelpText
              })]
            }), subtitle && /*#__PURE__*/_jsx("p", {
              children: subtitle
            })]
          }), renderedBody, renderedFooter]
        }));
      }
    }))
  }));
};

AbstractCard.propTypes = {
  bodyComponent: CustomRenderer.propType,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  footerComponent: CustomRenderer.propType,
  footerHoverlayContent: PropTypes.node,
  headingComponent: CustomRenderer.propType,
  iconUrl: PropTypes.string.isRequired,
  imageSectionClassName: PropTypes.string,
  installs: PropTypes.number,
  onClick: PropTypes.func,
  onSelectedChange: PropTypes.func,
  price: PropTypes.number,
  ribbonComponent: CustomRenderer.propType,
  selectable: PropTypes.bool,
  selected: PropTypes.bool,
  statusComponent: CustomRenderer.propType,
  subtitle: PropTypes.node,
  title: PropTypes.node,
  titleHelpText: PropTypes.node
};
AbstractCard.defaultProps = {
  disabled: false,
  headingComponent: DefaultHeader,
  selectable: false,
  selected: false,
  footerComponent: DefaultFooter
};
export default Controllable(AbstractCard, ['selected']);